import * as React from 'react';
import { Helmet } from 'react-helmet';

import { CardTypes } from 'defs/card-types';
import routes, { getBaseTitle, getSeoDataFromViewId } from 'defs/routes';
import { RouteType } from 'defs/routes';
import { push as dataLayerPush } from 'helper/ga';
import appConfig from 'helper/mapBackendConfig';
import { template } from 'helper/string';

import Loader from 'components/atoms/Loader';
import ProgramQuit from 'components/molecules/Program/ProgramQuit';
import TextBox from 'components/molecules/TextBox';
import Calendar from 'containers/molecules/Calendar';
import Card from 'containers/molecules/Card/ProgramFeed';
import OptionalCard from 'containers/molecules/OptionalCard';
import { mapBackendType } from 'store/optionalProgramItems';
import { TUTORIAL_ID, TutorialIdType } from 'types/Tutorial';

import { CoachIsEnumType } from 'components/molecules/CoachSelector';
import { OptionalProgramItemsEnum } from 'types/services/magnolia/enums/OptionalProgramItemsEnum';
import { WithRouterType } from 'types/withRouter';

import i18n from 'helper/i18n';

import { Flipper } from 'react-flip-toolkit';

import withVirtualPageView from 'components/hoc/withVirtualPageView';
import { WithVirtualPageView } from 'components/hoc/withVirtualPageView';
import VirtualPageViewFields from 'defs/ga/fields/virtualPageView';
import { TriggerTutorialOptionsType } from 'store/tutorial';

export type ProgramFeedViewCardProps = {
	id: number;
	type: string;
	isDone: boolean;
};

export type ProgramFeedViewFinishInfo = {
	todos: number;
	hours: number;
};

export type ProgramFeedViewProps = WithVirtualPageView & {
	matchingRouteConfig: RouteType;
	isLoadingView: boolean;
	currentDayNumber?: number;
	isLoadingActivePrograms: boolean;
	isLoadingManualFinish: boolean;
	isTodayAfterProgramm: boolean;
	isToday: boolean;
	hasLoadedActivePrograms: boolean;
	countActivePrograms: number;
	hasViewError: boolean;
	isAuthenticated: boolean;
	isDayCheckable: boolean | null;
	isDayInFuture: boolean;
	isAnamnesisDone: boolean;
	params: Object;
	isLastDay: boolean;
	isLastDay_DEPRECATED: boolean;
	doRequestView: Function;
	doChangeCurrentDay: Function;
	doRequestFinishManually: Function;
	coach: CoachIsEnumType;
	finishInfo?: ProgramFeedViewFinishInfo;
	cards?: [ProgramFeedViewCardProps];
	currentDay?: {
		programItems: any[];
	} | null;
	todayDayNumber?: number;
	numberOfDays?: number;
	optionalItems: OptionalProgramItemsEnum[];
	doTriggerTutorial: (flag: TutorialIdType, delay?: number, options?: TriggerTutorialOptionsType) => any;
	doRequestCalendar: () => void;
	program: any;
	level: string;
	levelPoints: string;
	startDate: string;
	endDate: string;
} & WithRouterType;

/**
 * Convert functional to class component
 * - only used in ProgramFeedView
 * @see PULSWM-3699
 */
class FunctionalToClassComponent extends React.Component<{ children: any }> {
	public static displayName: 'ClassBasedComponentHoc';
	public render() {
		return this.props.children;
	}
}

// tslint:disable: max-classes-per-file
export class ProgramFeedView extends React.Component<ProgramFeedViewProps> {
	public static displayName = 'ProgramFeedView';

	public disableAllAnimations = false;
	public tracked = false;

	public checkActivePrograms() {
		const { history, hasLoadedActivePrograms, countActivePrograms, isAnamnesisDone } = this.props;

		if (!hasLoadedActivePrograms || countActivePrograms !== 0 || !isAnamnesisDone) {
			return;
		}

		history.push(routes.program.recommended.href);
	}

	public componentDidMount() {
		const dayNumber = this.props.match.params.day ? { dayNumber: this.props.match.params.day } : null;
		this.props.doRequestCalendar();
		this.props.doRequestView(routes.today.indexRoute.id, dayNumber);
		this.checkActivePrograms();
		this.props.doTriggerTutorial(TUTORIAL_ID.FEED_OVERVIEW, 2000);
	}

	public componentDidUpdate(prevProps: ProgramFeedViewProps) {
		const { match } = this.props;
		if (this.props.match.params.day !== prevProps.match.params.day) {
			this.disableAllAnimations = true;

			if (this.isTodayRootRoute) {
				this.props.doChangeCurrentDay();
			} else {
				this.props.doChangeCurrentDay(match.params.day);
			}
		}

		this.disableAllAnimations = false;
		this.checkActivePrograms();
	}

	get isTodayRootRoute(): boolean {
		return !this.props.match.params.day;
	}

	public renderCards() {
		const { isDayCheckable, cards, isDayInFuture, isLastDay, isToday, currentDayNumber } = this.props;
		const disablePostponeButton = cards ? cards.length === 1 : false;

		const flipKey = cards?.map((item) => item.isDone).join('-');
		const spring = {
			stiffness: 23,
			damping: 10,
		};

		return (
			<Flipper flipKey={flipKey} spring={spring}>
				{(cards || ([] as ProgramFeedViewCardProps[])).map((card: ProgramFeedViewCardProps) => {
					return (
						<FunctionalToClassComponent key={`card-${card.type}-${card.id}`}>
							<Card
								{...({} as any)}
								key={`card-${card.type}-${card.id}`}
								id={card.id}
								isCheckable={isDayCheckable}
								isDayInFuture={isDayInFuture}
								isLastDay={isLastDay}
								isToday={isToday}
								currentDayNumber={currentDayNumber}
								disablePostponeButton={disablePostponeButton}
							/>
						</FunctionalToClassComponent>
					);
				})}
			</Flipper>
		);
	}

	public renderOptionalCards() {
		const { optionalItems, currentDayNumber } = this.props;

		return optionalItems.map((optionalItem: OptionalProgramItemsEnum) => {
			return (
				<OptionalCard
					key={optionalItem}
					currentDayNumber={currentDayNumber}
					type={mapBackendType(optionalItem)}
					id={optionalItem}
				/>
			);
		});
	}

	get canQuitManually(): boolean {
		const {
			isLoadingView,
			hasViewError,
			numberOfDays,
			todayDayNumber,
			finishInfo,
			isLastDay_DEPRECATED,
			isTodayAfterProgramm,
		} = this.props;

		if (!isLoadingView && !hasViewError && isTodayAfterProgramm) {
			return true;
		}

		return !!(
			!isLoadingView &&
			!hasViewError &&
			isLastDay_DEPRECATED &&
			finishInfo &&
			numberOfDays === todayDayNumber
		);
	}

	public renderCalendar() {
		const {
			coach,
			isDayInFuture,
			todayDayNumber,
			currentDayNumber,
			isDayCheckable,
			history,
			match,
			cards = [],
		} = this.props;
		const textboxTense = isDayInFuture ? 'future' : 'past';
		const saveCards = Array.isArray(cards) ? cards : ([] as ProgramFeedViewCardProps[]);
		const dayIsToday = todayDayNumber === currentDayNumber;
		const cardCheckList = saveCards.map(({ isDone }): boolean => isDone);
		const allCardsChecked = cardCheckList.every((isDone) => isDone === true);
		const allImportantCardsChecked = saveCards
			.map((card: ProgramFeedViewCardProps): ProgramFeedViewCardProps | void => {
				return !!~[CardTypes.DynamicProduct, CardTypes.Recommendation].indexOf(card.type) ? undefined : card;
			})
			.filter(Boolean)
			.map(({ isDone }: any): boolean => !!isDone)
			.every((isDone) => isDone === true);

		return (
			<div data-react-wrapper>
				<Calendar
					routeKey={window.location.href}
					navigate={history.push as any}
					selectedDayNumber={match.params.day ? parseInt(`${match.params.day}`, 10) : todayDayNumber || 1}
				/>
				{isDayCheckable !== null && !allCardsChecked && isDayCheckable === false && (
					<TextBox
						modifier={'coach'}
						title={i18n(`react.impulsCoach.views.programFeedView.uncheckable.${textboxTense}.title`)}
						text={i18n(`react.impulsCoach.views.programFeedView.uncheckable.${textboxTense}.text`)}
						coach={coach}
					/>
				)}
				{isDayCheckable && allImportantCardsChecked && dayIsToday && (
					<TextBox
						coach={coach}
						modifier={'coach'}
						gesture="front-perfect"
						title={i18n(`react.impulsCoach.views.programFeedView.todayAllItemsDone.title`)}
						text={i18n(`react.impulsCoach.views.programFeedView.todayAllItemsDone.text`)}
					/>
				)}
			</div>
		);
	}

	get seoData(): ReturnType<typeof getSeoDataFromViewId> {
		const { id } = this.props.matchingRouteConfig;
		return getSeoDataFromViewId(id);
	}

	get getProgramName(): string {
		if (this.props.program) {
			return this.props.program.title;
		}
		return '';
	}

	public render() {
		const {
			currentDay,
			isLoadingView,
			hasViewError,
			finishInfo,
			doRequestFinishManually,
			isLoadingManualFinish,
			trackVirtualPageView,
		} = this.props;

		const seoData = {
			...this.seoData,
			title: {
				singular: i18n(`react.impulsCoach.routes.today.day.title.singular`),
				plural: i18n(`react.impulsCoach.routes.today.day.title.plural`),
				default: i18n(`react.impulsCoach.routes.today.day.title`),
			},
		};

		const seoCompilationPlaceholders = {
			todos: currentDay ? currentDay.programItems.length : 0,
			dayNumber: this.props.todayDayNumber,
			numberOfDays: this.props.numberOfDays,
		};

		const decoratedSeoData = {
			title: i18n(
				'react.impulsCoach.routes.today.day.title',
				seoCompilationPlaceholders,
				seoCompilationPlaceholders.todos
			),
			og: {
				title: i18n(
					'react.impulsCoach.routes.today.day.title',
					seoCompilationPlaceholders,
					seoCompilationPlaceholders.todos
				),
			},
		};

		const SeoHeader = () => (
			<Helmet>
				<title>{`${decoratedSeoData.title} ${i18n('react.impulsCoach.routes.title.separator')} ${
					this.getProgramName
				}`}</title>
				{decoratedSeoData.title && (
					<meta property="og:title" content={template(seoData.og.title, seoCompilationPlaceholders)} />
				)}
			</Helmet>
		);

		if (isLoadingView || this.isTodayRootRoute) {
			return (
				<div data-react-wrapper>
					{this.renderCalendar()}
					<Loader />
				</div>
			);
		}

		if (!this.tracked) {
			trackVirtualPageView({
				[VirtualPageViewFields.VirtualPageTitle]: `${decoratedSeoData.title} ${getBaseTitle()}`,
			});
			this.tracked = true;
		}

		return (
			<div data-react-wrapper>
				{this.renderCalendar()}
				<SeoHeader />

				{this.canQuitManually && finishInfo && (
					<ProgramQuit
						loading={isLoadingManualFinish}
						doRequestFinishManually={doRequestFinishManually}
						todos={finishInfo.todos}
						hours={finishInfo.hours}
					/>
				)}
				{!isLoadingView && !hasViewError && this.renderOptionalCards()}
				{!isLoadingView && !hasViewError && this.renderCards()}
			</div>
		);
	}
}

export default withVirtualPageView(ProgramFeedView);
